import React, { useState, useCallback, FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { clusterApi } from "../services/api";
import WarningIcon from "@mui/icons-material/Warning";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import "./LoginPage.css";

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Resolve the first available client/domain for navigation
  const navigateToFirstClient = useCallback(async () => {
    try {
      const clients = await clusterApi.getClients();
      if (clients && clients.length > 0) {
        navigate(`/${clients[0].id}`);
      } else {
        navigate("/default");
      }
    } catch {
      navigate("/default");
    }
  }, [navigate]);

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigateToFirstClient();
    }
  }, [isAuthenticated, navigateToFirstClient]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      await login(email, password);
      await navigateToFirstClient();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-logo">
              <img src="/clusterview-logo.svg" alt="ClusterView" className="logo-icon" style={{ width: 64, height: 64 }} />
            </div>
            <h1>ClusterView</h1>
            <p className="login-subtitle">
              Sign in to analyze and visualize your keyword clusters.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            {error && (
              <div className="login-error">
                <WarningIcon className="error-icon" fontSize="small" />
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <div className="input-wrapper">
                <EmailIcon className="input-icon" fontSize="small" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <LockIcon className="input-icon" fontSize="small" />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="button-spinner"></span>
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          <div className="login-footer">
            <p className="login-forgot-link">
              <Link to="/forgot-password">Forgot Password?</Link>
            </p>
            <p className="login-signup-link">
              Don't have an account? <Link to="/signup">Sign Up</Link>
            </p>
            <p className="login-powered-by">Powered by AI-driven keyword analysis</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
